import React, { useCallback, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UploadSimple, FilePdf, MagicWand, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["labor", "travel", "utilities", "office", "equipment", "misc"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-caps mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

const empty = {
  vendor: "",
  amount: "",
  date: new Date().toISOString().slice(0, 10),
  category: "misc",
  project_id: "",
  description: "",
};

/**
 * Upload an expense slip / receipt (PDF or image). The backend auto-extracts
 * vendor / amount / date / category / description with a two-tier
 * pdfplumber-then-Gemini pipeline. On save we POST the metadata, then attach
 * the file to the newly created expense so users can view it later.
 */
export function ExpenseUploadSheet({ projects, onUploaded, open: openProp, onOpenChange, hideTrigger }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChange || setOpenInternal;
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseErr, setParseErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setForm(empty); setFile(null); setParseErr(""); };

  const parseSlip = useCallback(async (chosen) => {
    if (!chosen) return;
    setParsing(true); setParseErr("");
    try {
      const fd = new FormData();
      fd.append("file", chosen);
      const { data } = await api.post("/expenses/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setForm((prev) => ({
        ...prev,
        vendor: prev.vendor || data.vendor || "",
        amount: prev.amount || (data.amount ? String(data.amount) : ""),
        date: data.date || prev.date,
        category: CATEGORIES.includes(data.category) ? data.category : prev.category,
        description: prev.description || data.description || "",
      }));
      toast.success("Details extracted — please review");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setParseErr(msg);
      toast.warning(`Auto-fill failed: ${msg}. Enter manually.`);
    } finally {
      setParsing(false);
    }
  }, []);

  const onFilePicked = (e) => {
    const chosen = e.target.files?.[0] || null;
    setFile(chosen);
    setParseErr("");
    if (chosen) parseSlip(chosen);
  };

  const submit = async () => {
    if (!form.vendor || !form.amount || !form.date) {
      toast.error("Vendor, amount and date are required");
      return;
    }
    setBusy(true);
    try {
      const { data: created } = await api.post("/expenses", {
        vendor: form.vendor,
        amount: Number(form.amount),
        date: form.date,
        category: form.category || "misc",
        description: form.description || "",
        project_id: form.project_id || null,
      });
      if (file && created?.id) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("parent_type", "expense");
        fd.append("parent_id", created.id);
        fd.append("category", "general");
        fd.append("title", `Receipt · ${form.vendor}`);
        try {
          await api.post("/attachments", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (attErr) {
          devLog.warn("Attachment upload failed", attErr);
        }
      }
      toast.success("Expense saved");
      setOpen(false);
      reset();
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <Button data-testid="upload-expense-button" variant="outline" className="rounded-sm border-slate-300">
            <UploadSimple size={16} className="mr-2" /> Upload Slip
          </Button>
        </SheetTrigger>
      )}
      <SheetContent className="w-[440px] overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Upload Expense Slip</SheetTitle></SheetHeader>
        <div className="text-[11.5px] text-slate-500 mt-1 mb-5">
          Attach a receipt / bill / slip — vendor, amount and date will be auto-extracted.
        </div>

        <div className="space-y-4">
          <Field label={
            <span className="flex items-center gap-2">
              Receipt
              <span className="inline-flex items-center gap-1 text-[9px] tracking-widest font-semibold uppercase bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                <MagicWand size={10} /> AI auto-fill
              </span>
            </span>
          }>
            <label
              htmlFor="expense-slip-input"
              className="flex items-center gap-3 border border-dashed border-slate-300 hover:border-[#EA580C] rounded-sm p-3 cursor-pointer bg-slate-50"
            >
              {parsing ? <CircleNotch size={22} className="text-[#EA580C] animate-spin" /> : <FilePdf size={22} className="text-[#EA580C]" />}
              <div className="flex-1 min-w-0">
                {file ? (
                  <>
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                      {parsing && <span className="text-[#EA580C] ml-2">· Extracting…</span>}
                      {!parsing && !parseErr && form.vendor && <span className="text-emerald-700 ml-2">· Auto-filled ✓</span>}
                      {!parsing && parseErr && <span className="text-red-600 ml-2">· Manual entry needed</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium text-slate-700">Click to choose receipt</div>
                    <div className="text-[11px] text-slate-500">PDF / JPG / PNG — max 20 MB</div>
                  </>
                )}
              </div>
              <input
                id="expense-slip-input"
                data-testid="expense-slip-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={onFilePicked}
                className="hidden"
              />
            </label>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="upload-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount (₹)">
              <Input
                type="number" step="0.01"
                data-testid="upload-expense-amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Vendor">
            <Input
              data-testid="upload-expense-vendor"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            />
          </Field>

          <Field label="Date">
            <Input
              type="date"
              data-testid="upload-expense-date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Field>

          <Field label="Project (optional)">
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>

          <Button
            onClick={submit}
            disabled={busy}
            data-testid="save-uploaded-expense"
            className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800"
          >
            {busy ? "Saving…" : "Save Expense"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
